import axios from 'axios';
import { getToken, setToken, removeToken } from '../utils/storage';
import { t } from '../context/LocaleContext';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:19000/api/v1';

export const client = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams();
    for (const key in params) {
      if (Array.isArray(params[key])) {
        params[key].forEach((val: any) => searchParams.append(key, String(val)));
      } else if (params[key] !== undefined && params[key] !== null) {
        searchParams.append(key, String(params[key]));
      }
    }
    return searchParams.toString();
  },
});

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

client.interceptors.request.use((config) => {
  const token = getToken();
  // 登录接口不携带旧token
  const isLoginRoute = config.url && config.url.includes('/auth/login');
  if (token && !isLoginRoute) config.headers.Authorization = `Bearer ${token}`;
  // 上传 FormData 时让浏览器自动设置 Content-Type（含 boundary）
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

client.interceptors.response.use(
  (res) => res.data,
  async (err) => {
    const originalRequest = err.config;
    const data = err.response?.data;
    const isLoginPage = window.location.pathname === '/login';

    // 处理认证错误，尝试自动刷新 token
    // 避免刷新 token 请求本身也触发刷新导致死锁
    const isRefreshRequest = originalRequest?.url?.includes('/auth/refresh');
    const isAuthError = data?.code === 401001 || data?.code === 400005 || err.response?.status === 401;
    if (isAuthError && !isLoginPage && !isRefreshRequest && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshRes: any = await client.post('/auth/refresh');
          const newToken = refreshRes?.data?.token || refreshRes?.token;
          const expiresIn = refreshRes?.data?.expires_in || refreshRes?.expires_in || 2592000;
          if (newToken) {
            setToken(newToken, expiresIn * 1000);
            onRefreshed(newToken);
          }
          isRefreshing = false;
          return client(originalRequest);
        } catch (refreshErr) {
          isRefreshing = false;
          onRefreshed(''); // 通知队列中的请求刷新失败
          removeToken();
          // 公开分享页面不强制跳转登录
          const publicPaths = ['/supplier/confirm', '/purchase/batch-share', '/quote-view'];
          const isPublicPage = publicPaths.some(p => window.location.pathname.startsWith(p));
          if (!isPublicPage) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshErr);
        }
      }

      // 正在刷新中，加入队列等待
      return new Promise((resolve, reject) => {
        addRefreshSubscriber((token: string) => {
          if (!token) {
            reject(new Error('Token refresh failed'));
            return;
          }
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(client(originalRequest));
        });
      });
    }

    // 格式化错误信息
    let errorMessage: string;
    const status = err.response?.status;

    if (data && typeof data === 'object') {
      errorMessage = data.message || data.error || JSON.stringify(data);
    } else if (typeof data === 'string') {
      // 后端返回 HTML（如 Nginx/Cloudflare 错误页或 500 堆栈页面）
      if (data.trim().startsWith('<')) {
        if (status === 502 || status === 503 || status === 504) {
          errorMessage = t('服务器暂时不可用，请稍后重试');
        } else if (status === 500) {
          errorMessage = t('服务器内部错误，请联系管理员');
        } else if (status === 404) {
          errorMessage = t('请求接口不存在');
        } else {
          errorMessage = `服务器错误 (HTTP ${status || 'Unknown'})`;
        }
      } else {
        errorMessage = data;
      }
    } else if (err.message) {
      errorMessage = err.message;
    } else {
      errorMessage = t('请求失败，请稍后重试');
    }

    const rejectError: any = new Error(errorMessage);
    rejectError.status = status;
    rejectError.code = data?.code;
    rejectError.responseData = data;
    // 保留请求取消 / 超时的原始标记，便于业务层识别
    if (axios.isCancel(err) || err.code === 'ERR_CANCELED' || err.code === 'ERR_ABORTED' || err.code === 'ECONNABORTED') {
      rejectError.code = err.code;
      rejectError.name = err.name;
    }
    return Promise.reject(rejectError);
  }
);
